/**
 * MessageBubble.js — single message bubble.
 *
 * Pure presentational. Handles all message types: text, media, system,
 * template, interactive, reaction.
 */

import { html } from 'htm/preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import { useStore } from '@nanostores/preact';
import { $reactions } from '../stores/messages.js';
import { showToast } from '../stores/ui.js';
import { FormattedText, formatWaText } from '../shared/wa-format.js';
import { DIRECTION, MESSAGE_STATUS } from '../shared/constants.js';
import { hapticReact } from '../services/haptics.js';
import { MediaLightbox } from './MediaLightbox.js';

const REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

const TICKS = {
  pending:   '○',
  sent:      '✓',
  delivered: '✓✓',
  read:      '✓✓',
  failed:    '✗',
};

function formatMsgTime(ts) {
  if (!ts) return '';
  // Three shapes flow in here:
  //   1. Number  — outbound optimistic sends write Math.floor(Date.now()/1000)
  //   2. Numeric string — the inbox-proxy returns inbox_messages.timestamp
  //      as a string (PostgREST default for bigint columns). Previously
  //      new Date("1779696324") returned Invalid Date → no time rendered
  //      on any inbound bubble.
  //   3. ISO string — defensive, not currently produced anywhere.
  // Coerce numeric strings to Number first; only fall back to the raw
  // Date(string) path when the value isn't numeric at all.
  let n = typeof ts === 'number' ? ts : Number(ts);
  let d;
  if (Number.isFinite(n)) {
    if (n < 1e10) n *= 1000; // unix seconds → ms
    d = new Date(n);
  } else {
    d = new Date(ts);
  }
  if (isNaN(d)) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** Only allow safe URL schemes for img src — blocks javascript:, data: tracking, etc. */
function safeImageUrl(url) {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (/^(https?:\/\/|blob:|\/)/i.test(trimmed)) return trimmed;
  return '';
}

/**
 * S24 — Meta's webhook payload for inbound media embeds a signed lookaside
 * URL (`lookaside.fbsbx.com/whatsapp_business/attachments/?mid=...&ext=...`)
 * that expires in ~5-30 minutes. Using it directly as `<img src>` works
 * briefly then 401s forever, showing the "Preview unavailable" placeholder.
 *
 * Treat any lookaside URL as ephemeral and fall through to proxyMediaUrl,
 * which lets the backend resolve against Meta freshly via the channel
 * token. Once media-archive-background (S25) finishes mirroring the
 * bytes to Supabase Storage, it PATCHes inbox_messages.body.<type>.link
 * with the permanent Supabase URL — realtime message_update fires and
 * the bubble re-renders from the permanent URL without hitting the
 * lookaside at all.
 *
 * Outbound S25 URLs (Supabase Storage public URLs) are our own stable
 * hosts and pass through untouched.
 */
function isEphemeralMetaUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return /lookaside\.fbsbx\.com|whatsapp_business\/attachments/i.test(url);
}

/**
 * Build a proxy URL that resolves a Meta media_id to bytes via the
 * /media/{id} backend route. Customer-sent media has only `id` (no `link`)
 * — see Phase 1.4 task #53. The `from` query param is the phone_number_id
 * the message was received on (= the channel that needs the token to
 * fetch the media from Meta).
 */
function proxyMediaUrl(mediaId, channelPhoneId) {
  if (!mediaId || !channelPhoneId) return '';
  return `/.netlify/functions/inbox-proxy?path=/media/${encodeURIComponent(mediaId)}&from=${encodeURIComponent(channelPhoneId)}`;
}

/** Pick the right channel phone number for a message, regardless of direction. */
function _channelPhoneForMsg(msg) {
  // Inbound: the channel is the recipient (toPhone). Outbound: the channel is
  // the sender (fromPhone). Either way, it's the phone_number_id of OUR channel.
  return msg.fromMe || msg.direction === 'outbound'
    ? (msg.fromPhone || msg.toPhone)
    : (msg.toPhone || msg.fromPhone);
}

/**
 * Group reactions by emoji, returning [{emoji, count, mine}] for chip rendering.
 */
function _groupReactions(reactions) {
  const grouped = {};
  for (const r of reactions || []) {
    if (!r.emoji) continue;
    if (!grouped[r.emoji]) grouped[r.emoji] = { emoji: r.emoji, count: 0, mine: false };
    grouped[r.emoji].count += 1;
    if (r.fromMe) grouped[r.emoji].mine = true;
  }
  return Object.values(grouped);
}

export function MessageBubble({
  msg,
  phone,
  // S5 — composite thread key. Reactions are keyed by
  // `${phone}:${channelPhoneId}::${wamid}` so bubbles from the same
  // contact on two channels each look up their own reactions.
  channelPhoneId,
  replyToPreview,
  onReply,
  onReact,
  // #161 — invoked when the user taps the reply-quote header on a
  // bubble that is itself a reply. Thread handles the scroll + flash.
  // Receives the target wamid (what this message was replying to).
  onJumpToReply,
  groupedTop = false,
  groupedBottom = false,
  // #101 — when the chat's 24h session window is closed, no new
  // reactions can be sent or removed (Meta would reject them
  // server-side anyway). The bubble still RENDERS existing reactions
  // for context; only the interaction handlers go dead.
  interactionsLocked = false,
}) {
  const [reactionShelfOpen, setReactionShelfOpen] = useState(false);
  // S20 — lightbox state. Set to { url, mimeType, filename } when the user
  // taps the image/video preview; cleared on close.
  const [lightbox, setLightbox] = useState(null);
  const allReactions = useStore($reactions);

  // Look up reactions under BOTH wamid AND metaWamid keys. Outbound messages
  // have wamid=queue_id and metaWamid=Meta wamid; customer reactions reference
  // the Meta wamid, so we need to merge results from both keys.
  const threadPrefix = channelPhoneId ? `${phone}:${channelPhoneId}` : String(phone || '');
  const wamidKey     = msg.wamid     ? `${threadPrefix}::${msg.wamid}`     : null;
  const metaWamidKey = msg.metaWamid ? `${threadPrefix}::${msg.metaWamid}` : null;
  const reactionList = _groupReactions([
    ...(wamidKey     && allReactions[wamidKey]     ? allReactions[wamidKey]     : []),
    ...(metaWamidKey && allReactions[metaWamidKey] ? allReactions[metaWamidKey] : []),
  ]);

  const handleContextMenu = (e) => {
    e.preventDefault();
    if (msg.direction === DIRECTION.SYSTEM) return;
    if (interactionsLocked) return; // #101 — session window closed
    setReactionShelfOpen(true);
  };

  // ── Mobile long-press → reaction shelf (#77) ───────────────────────────
  // Touch long-press is the iOS/Android native gesture for "more options".
  // onContextMenu fires inconsistently on touch (iOS Safari shows its own
  // selection menu, Android Chrome only fires after a 2s+ hold), so we
  // implement the gesture explicitly using touch events.
  //
  // Pattern:
  //   touchstart  → record start position + start 500ms timer
  //   touchmove   → if finger moves >10px, cancel (it was a scroll)
  //   touchend    → cancel (it was a tap or short hold)
  //   timer fires → vibrate + open the shelf, swallow the upcoming click
  //
  // The iOS callout (text-selection menu) is suppressed via the
  // user-select-none style on the bubble itself — see the inline style.
  const longPressTimer = useRef(null);
  const longPressStart = useRef(null);
  const longPressFired = useRef(false);

  // Belt-and-braces: clear the timer if the bubble unmounts mid-press
  useEffect(() => () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  }, []);

  const handleTouchStart = (e) => {
    if (msg.direction === DIRECTION.SYSTEM) return;
    if (interactionsLocked) return; // #101 — session window closed
    longPressFired.current = false;
    const t = e.touches?.[0];
    if (!t) return;
    longPressStart.current = { x: t.clientX, y: t.clientY };
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      hapticReact();
      setReactionShelfOpen(true);
    }, 500);
  };
  const handleTouchMove = (e) => {
    if (!longPressTimer.current || !longPressStart.current) return;
    const t = e.touches?.[0];
    if (!t) return;
    const dx = t.clientX - longPressStart.current.x;
    const dy = t.clientY - longPressStart.current.y;
    if (dx * dx + dy * dy > 100) { // >10px movement = scroll, cancel
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };
  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };
  // Swallow the click that follows a long-press fire so the bubble doesn't
  // trigger any tap action (e.g. media open) right after the shelf appears.
  const handleClickCapture = (e) => {
    if (longPressFired.current) {
      e.stopPropagation();
      e.preventDefault();
      longPressFired.current = false;
    }
  };

  const handleReact = (emoji) => {
    setReactionShelfOpen(false);
    if (onReact) onReact(emoji);
  };

  const handleReply = () => {
    setReactionShelfOpen(false);
    if (onReply) onReply();
  };

  // Extract a copyable string from any message shape. Plain text, media
  // captions, location names, contact card text, interactive titles and
  // button-reply / list-reply / nfm_reply selections — anything that has
  // a clear textual representation goes here. Returns '' when the message
  // has no copyable text (pure media without a caption, reaction, etc).
  const _copyableText = () => {
    const t = msg.type || 'text';
    if (t === 'text' || t === 'chat') return msg.text?.body || '';
    if (t === 'image')    return msg.image?.caption || '';
    if (t === 'video')    return msg.video?.caption || '';
    if (t === 'audio' || t === 'voice') return '';
    if (t === 'document') return msg.document?.caption || msg.document?.filename || '';
    if (t === 'location') {
      const l = msg.location || {};
      return [l.name, l.address].filter(Boolean).join('\n');
    }
    if (t === 'contacts') {
      const first = Array.isArray(msg.contacts) ? msg.contacts[0] : msg.contacts;
      const lines = [
        first?.name?.formatted_name || first?.name?.first_name,
        first?.phones?.[0]?.phone,
        first?.emails?.[0]?.email,
      ].filter(Boolean);
      return lines.join('\n');
    }
    if (t === 'interactive') {
      const it = msg.interactive || {};
      if (it.button_reply?.title) return it.button_reply.title;
      if (it.list_reply?.title) {
        return it.list_reply.description
          ? `${it.list_reply.title} — ${it.list_reply.description}`
          : it.list_reply.title;
      }
      if (it.nfm_reply?.response_json) {
        try {
          const parsed = JSON.parse(it.nfm_reply.response_json);
          if (parsed && typeof parsed === 'object') {
            const SKIP = new Set(['flow_token', 'version']);
            return Object.entries(parsed)
              .filter(([k, v]) => !SKIP.has(k) && v != null && v !== '')
              .map(([k, v]) => `${String(k).replace(/^screen_\d+_/, '').replace(/_/g, ' ')}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
              .join('\n');
          }
        } catch { /* fall through */ }
      }
      // Outbound interactive — copy the body text customers see.
      return it.body?.text || it.header?.text || '';
    }
    if (t === 'template') {
      // Legacy rendered preview wins; otherwise the param values we display.
      if (msg.text?.body) return msg.text.body;
      const comps = Array.isArray(msg.template?.components) ? msg.template.components : [];
      const bodyC = comps.find(c => (c.type || '').toLowerCase() === 'body');
      const vals = (bodyC?.parameters || []).map(p => {
        const pt = (p.type || '').toLowerCase();
        if (pt === 'text')      return p.text || '';
        if (pt === 'currency')  return `${p.currency?.code || ''} ${((p.currency?.amount_1000 || 0) / 1000)}`.trim();
        if (pt === 'date_time') return p.date_time?.fallback_value || '';
        return '';
      }).filter(Boolean);
      return vals.join(' · ');
    }
    return '';
  };

  const handleCopy = async () => {
    setReactionShelfOpen(false);
    const text = _copyableText();
    if (!text) { showToast('Nothing to copy', 1400); return; }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // iOS pre-WKWebView 13.4 / very old Android fallback. Render an
        // offscreen textarea, select, execCommand. The reaction shelf is
        // already closed so the focus shift won't disrupt anything visible.
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.top = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      hapticReact();
      showToast('Copied', 1400);
    } catch (e) {
      showToast('Copy failed', 1800);
    }
  };


  // System dividers (assignment, mark done, close)
  if (msg.direction === DIRECTION.SYSTEM || msg.type === 'system') {
    const text = msg.text?.body || msg.preview || 'System event';
    let cls = '';
    if (text.includes('Assigned') || text.includes('Unassigned')) cls = 'inbox-divider-assign';
    else if (text.includes('done')) cls = 'inbox-divider-done';
    else if (text.includes('closed') || text.includes('Closed')) cls = 'inbox-divider-close';
    return html`<div class="inbox-system-divider ${cls}">${text}</div>`;
  }

  const isOut    = msg.fromMe || msg.direction === DIRECTION.OUTBOUND;
  const wrapClass = isOut ? 'inbox-bubble-wrap-out' : 'inbox-bubble-wrap-in';
  let bubbleClass = isOut ? 'inbox-bubble-out' : 'inbox-bubble-in';
  if (groupedTop)    bubbleClass += ' inbox-bubble-grouped-top';
  if (groupedBottom) bubbleClass += ' inbox-bubble-grouped-bottom';

  // Body content by type
  let bodyContent;
  const type = msg.type || 'text';

  // Template bubbles get an extra modifier class so the bubble surface
  // itself can carry a "card / letterhead" treatment distinct from a
  // regular text bubble (brand-green left rail, slightly different
  // background gradient, subtle inset border). See inbox-v2.css.
  if (type === 'template') bubbleClass += ' inbox-bubble-template';

  // Channel phone for media-fetch fallback (inbound media has no `link`,
  // only Meta `id` — we resolve via the proxy /media/{id} route).
  const channelPhone = _channelPhoneForMsg(msg);

  if (type === 'text' || type === 'chat') {
    const text = msg.text?.body || '';
    bodyContent = html`<${FormattedText} text=${text} />`;
  } else if (type === 'image') {
    // S25 — preview = thumbnail_url (Supabase render transform, ~30-60KB
    // for typical phone photos); tap target = full-res `link`. Legacy
    // rows without thumbnail_url fall back to `link` or the proxy
    // /media/{id} route so old messages still render.
    const rawUrl    = msg.image?.link || msg.image?.url || '';
    const directUrl = isEphemeralMetaUrl(rawUrl) ? '' : safeImageUrl(rawUrl);
    const fullUrl   = directUrl
      || (msg.image?.id ? proxyMediaUrl(msg.image.id, channelPhone) : '');
    const thumbUrl  = safeImageUrl(msg.image?.thumbnail_url || '') || fullUrl;
    const openLightbox = () => {
      if (!fullUrl) return;
      setLightbox({ url: fullUrl, mimeType: msg.image?.mime_type || 'image/jpeg', filename: msg.image?.filename || '' });
    };
    bodyContent = html`
      ${thumbUrl
        ? html`<div style="position:relative;display:inline-block">
                 <img
                   src=${thumbUrl}
                   loading="lazy"
                   onClick=${openLightbox}
                   onError=${(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentNode.nextElementSibling && (e.currentTarget.parentNode.nextElementSibling.style.display = 'block'); }}
                   style="max-width:240px;border-radius:10px;display:block;background:rgba(255,255,255,0.04);cursor:zoom-in"
                   alt="image"
                 />
                 ${fullUrl && html`<a
                   href=${fullUrl}
                   download=${msg.image?.filename || 'image.jpg'}
                   target="_blank"
                   rel="noopener"
                   onClick=${(e) => e.stopPropagation()}
                   title="Download"
                   aria-label="Download image"
                   style="position:absolute;top:6px;right:6px;width:30px;height:30px;border-radius:50%;background:rgba(0,0,0,0.55);color:#fff;display:flex;align-items:center;justify-content:center;text-decoration:none;-webkit-tap-highlight-color:transparent"
                 ><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg></a>`}
               </div>
               <div style="display:none;font-size:0.8rem;opacity:0.6;padding:8px">Preview unavailable · tap to retry</div>`
        : html`<div style="font-size:0.85rem;opacity:0.7">[Image]</div>`}
      ${msg.image?.caption && html`<div style="margin-top:0.4rem;font-size:0.85rem"><${FormattedText} text=${msg.image.caption} /></div>`}
    `;
  } else if (type === 'video') {
    // S25 — <video poster> uses thumbnail_url when available for a fast
    // visual preview before the video bytes start streaming. Tap-to-
    // expand opens the full original in the lightbox for download.
    const rawUrl    = msg.video?.link || msg.video?.url || '';
    const directUrl = isEphemeralMetaUrl(rawUrl) ? '' : safeImageUrl(rawUrl);
    const finalUrl  = directUrl
      || (msg.video?.id ? proxyMediaUrl(msg.video.id, channelPhone) : '');
    const poster    = safeImageUrl(msg.video?.thumbnail_url || '');
    const mime      = msg.video?.mime_type || 'video/mp4';
    const openLightbox = () => {
      if (!finalUrl) return;
      setLightbox({ url: finalUrl, mimeType: mime, filename: msg.video?.filename || '' });
    };
    bodyContent = html`
      ${finalUrl
        ? html`<div style="position:relative;display:inline-block">
                 <video controls playsinline preload="metadata" poster=${poster || undefined}
                   style="max-width:280px;border-radius:10px;display:block;background:#000">
                   <source src=${finalUrl} type=${mime} />
                 </video>
                 <a
                   href=${finalUrl}
                   download=${msg.video?.filename || 'video.mp4'}
                   target="_blank"
                   rel="noopener"
                   onClick=${(e) => e.stopPropagation()}
                   title="Download"
                   aria-label="Download video"
                   style="position:absolute;top:6px;right:42px;width:30px;height:30px;border-radius:50%;background:rgba(0,0,0,0.55);color:#fff;display:flex;align-items:center;justify-content:center;text-decoration:none;-webkit-tap-highlight-color:transparent"
                 ><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg></a>
                 <button onClick=${openLightbox}
                   style="position:absolute;top:6px;right:6px;width:30px;height:30px;border-radius:50%;border:none;background:rgba(0,0,0,0.55);color:#fff;font-size:0.75rem;cursor:pointer;display:flex;align-items:center;justify-content:center"
                   title="Open full size"
                   aria-label="Open full size"
                 >⤢</button>
               </div>`
        : html`<div style="font-size:0.85rem;opacity:0.8">🎬 ${msg.video?.filename || 'Video'}</div>`}
      ${msg.video?.caption && html`<div style="margin-top:0.4rem;font-size:0.85rem"><${FormattedText} text=${msg.video.caption} /></div>`}
    `;
  } else if (type === 'audio' || type === 'voice') {
    const rawUrl    = msg.audio?.link || msg.audio?.url || '';
    const directUrl = isEphemeralMetaUrl(rawUrl) ? '' : safeImageUrl(rawUrl);
    const finalUrl  = directUrl
      || (msg.audio?.id ? proxyMediaUrl(msg.audio.id, channelPhone) : '');
    const mime = msg.audio?.mime_type || 'audio/ogg';
    const audioFilename = msg.audio?.filename || (type === 'voice' ? 'voice-note.ogg' : 'audio.ogg');
    bodyContent = finalUrl
      ? html`<div>
               <audio controls preload="metadata" style="max-width:260px;display:block">
                 <source src=${finalUrl} type=${mime} />
               </audio>
               <a href=${finalUrl} download=${audioFilename} target="_blank" rel="noopener"
                 style="display:inline-flex;align-items:center;gap:4px;margin-top:6px;font-size:0.75rem;opacity:0.75;text-decoration:none;color:inherit"
                 aria-label="Download audio">
                 <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                 Download
               </a>
             </div>`
      : html`<div style="font-size:0.85rem;opacity:0.8">🔊 Audio message</div>`;
  } else if (type === 'document') {
    const rawUrl    = msg.document?.link || msg.document?.url || '';
    const directUrl = isEphemeralMetaUrl(rawUrl) ? '' : safeImageUrl(rawUrl);
    const finalUrl  = directUrl
      || (msg.document?.id ? proxyMediaUrl(msg.document.id, channelPhone) : '');
    const filename = msg.document?.filename || 'Document';
    bodyContent = finalUrl
      ? html`<a href=${finalUrl} target="_blank" rel="noopener" download=${filename} style="display:inline-flex;align-items:center;gap:6px;text-decoration:none;color:inherit;background:rgba(255,255,255,0.05);padding:8px 12px;border-radius:8px">
               <span style="font-size:1.1rem">📄</span>
               <span style="font-size:0.85rem;text-decoration:underline">${filename}</span>
             </a>${msg.document?.caption && html`<div style="margin-top:0.3rem;font-size:0.8rem;opacity:0.85"><${FormattedText} text=${msg.document.caption} /></div>`}`
      : html`<div style="font-size:0.85rem">📄 ${filename}</div>`;
  } else if (type === 'location') {
    // Phase 4.10: render a richer card with name, address, and a maps link.
    // The link uses Google Maps' universal lat/lng URL so it works on any
    // device that has a maps app installed (iOS opens Apple Maps on supported
    // UAs, Android opens Google Maps, web opens google.com/maps).
    const loc = msg.location || {};
    const mapsUrl = (loc.latitude != null && loc.longitude != null)
      ? `https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`
      : null;
    bodyContent = html`
      <div style="font-size:0.85rem;line-height:1.4">
        <div style="display:flex;align-items:center;gap:6px;font-weight:600;margin-bottom:${loc.address ? '3px' : '0'}">
          <span style="font-size:1.1rem">📍</span>
          <span>${loc.name || 'Location'}</span>
        </div>
        ${loc.address && html`<div style="font-size:0.78rem;opacity:0.8">${loc.address}</div>`}
        ${mapsUrl && html`
          <a href=${mapsUrl} target="_blank" rel="noopener"
             style="display:inline-block;margin-top:6px;font-size:0.75rem;color:#60a5fa;text-decoration:underline">
            Open in maps →
          </a>
        `}
      </div>
    `;
  } else if (type === 'sticker') {
    // Stickers are 512×512 webp/lottie. Customer-sent ones arrive with only
    // `id` (no link) so we resolve via the same /media/{id} proxy route as
    // images. We render small (~120px) since stickers are reactions/asides,
    // not main content. Animated stickers are static webp animations — the
    // browser plays them automatically when the file is .webp animated.
    const rawUrl    = msg.sticker?.link || msg.sticker?.url || '';
    const directUrl = isEphemeralMetaUrl(rawUrl) ? '' : safeImageUrl(rawUrl);
    const finalUrl  = directUrl
      || (msg.sticker?.id ? proxyMediaUrl(msg.sticker.id, channelPhone) : '');
    bodyContent = finalUrl
      ? html`<img src=${finalUrl} loading="lazy" alt="sticker"
          style="width:120px;height:120px;object-fit:contain;display:block;background:transparent" />`
      : html`<div style="font-size:1.6rem;opacity:0.85">🏷️</div>`;
  } else if (type === 'contacts') {
    // Phase 4.10 — agent-sent vcards. Render the first contact's formatted
    // name + first phone + optional company/email/title. Tapping the phone
    // triggers the native dialer via tel: URL.
    const first = Array.isArray(msg.contacts) ? msg.contacts[0] : msg.contacts;
    const name  = first?.name?.formatted_name || first?.name?.first_name || 'Contact';
    const phone = first?.phones?.[0]?.phone || first?.phones?.[0]?.wa_id || '';
    const email = first?.emails?.[0]?.email || '';
    const org   = first?.org?.company || '';
    const title = first?.org?.title || '';
    bodyContent = html`
      <div style="font-size:0.85rem;line-height:1.45;background:rgba(255,255,255,0.05);padding:10px 12px;border-radius:10px;min-width:180px">
        <div style="display:flex;align-items:center;gap:8px">
          <span style="display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:50%;background:rgba(37,211,102,0.18);font-size:1rem">👤</span>
          <div style="flex:1;min-width:0">
            <div style="font-weight:600">${name}</div>
            ${(org || title) && html`<div style="font-size:0.72rem;opacity:0.75">${[title, org].filter(Boolean).join(' · ')}</div>`}
          </div>
        </div>
        ${phone && html`
          <a href=${`tel:${phone}`}
             style="display:block;margin-top:8px;font-size:0.78rem;color:#60a5fa;text-decoration:none">
            📞 ${phone}
          </a>
        `}
        ${email && html`
          <a href=${`mailto:${email}`}
             style="display:block;margin-top:2px;font-size:0.78rem;color:#60a5fa;text-decoration:none">
            ✉️ ${email}
          </a>
        `}
      </div>
    `;
  } else if (type === 'reaction') {
    // Defensive — realtime now routes reactions to $reactions, so this row
    // shouldn't reach the thread message list. If a stale reaction message
    // is loaded from the historical inbox_messages query, hide it.
    return null;
  } else if (type === 'deleted') {
    bodyContent = html`<em style="opacity:0.6">This message was deleted</em>`;
  } else if (type === 'unsupported') {
    // R3 — Meta "unsupported" payload. Two common causes:
    //   1. WhatsApp Business App + Cloud API coexistence — customer sent
    //      from their native WA app; the content stays on-device and
    //      Meta sends only a stub notification to the Cloud API webhook.
    //   2. New Meta message type (polls, payments, new sticker formats)
    //      that the 1automations webhook wrapper hasn't added support
    //      for yet.
    // Either way the payload carries only an errors[] array, no content
    // we can render. Surface the Meta error code + title so an agent or
    // engineer can look it up. Raw frame is captured in
    // inbox_unsupported_log for future analysis.
    const err       = Array.isArray(msg.errors) ? msg.errors[0] : null;
    const errCode   = err?.code || null;
    const errTitle  = (err?.title || err?.message || 'Unknown type').toString();
    bodyContent = html`
      <div style="display:flex;align-items:flex-start;gap:10px;padding:4px 2px;font-size:0.85rem;opacity:0.9">
        <span style="font-size:1.1rem;line-height:1.1">📲</span>
        <div style="line-height:1.35">
          <div>${errTitle}${errCode ? html` <span style="opacity:0.55;font-size:0.72rem">· Meta ${errCode}</span>` : null}</div>
          <div style="font-size:0.72rem;opacity:0.65;margin-top:2px">Open WhatsApp on your phone to view the full message</div>
        </div>
      </div>
    `;
  } else if (type === 'template') {
    // Templates land here from two sources:
    //   1. Pre-#100 inbox sends — stashed `msg.text.body` with the rendered
    //      preview. Use it verbatim when present (we don't have components).
    //   2. Outbound echoes from the platform (Partner Panel campaigns,
    //      external senders on the WABA) — inbox-webhook stores the full
    //      Meta send payload at msg.template = {name, language, components}.
    //      We render the structure directly: header (text/image/video/doc),
    //      body params, footer, buttons. No Meta API roundtrip needed.
    const tpl       = msg.template || {};
    const tplName   = tpl.name || '';
    const tplLang   = tpl.language?.code || tpl.language || '';
    const comps     = Array.isArray(tpl.components) ? tpl.components : [];
    const headerC   = comps.find(c => (c.type || '').toLowerCase() === 'header');
    const bodyC     = comps.find(c => (c.type || '').toLowerCase() === 'body');
    const footerC   = comps.find(c => (c.type || '').toLowerCase() === 'footer');
    const buttonCs  = comps.filter(c => (c.type || '').toLowerCase() === 'button');
    const carouselC = comps.find(c => (c.type || '').toLowerCase() === 'carousel');
    const ltoC      = comps.find(c => (c.type || '').toLowerCase() === 'limited_time_offer');
    const legacyTxt = msg.text?.body || '';
    const hasStruct = headerC || (bodyC?.parameters?.length) || footerC || buttonCs.length || carouselC || ltoC;
    const humanName = tplName
      ? tplName.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase())
      : '';
    const tplFg     = isOut ? 'rgba(255,255,255,0.95)' : '#60a5fa';
    const tplBdr    = isOut ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.18)';

    // Header — render media inline, text as bold line.
    let tplHeader = null;
    if (headerC) {
      const p = headerC.parameters?.[0] || {};
      const ptype = (p.type || '').toLowerCase();
      if (ptype === 'image' && (p.image?.link || p.image?.url)) {
        const u = safeImageUrl(p.image.link || p.image.url);
        if (u) tplHeader = html`<img src=${u} loading="lazy" alt="header"
          style="max-width:240px;border-radius:8px;display:block;margin-bottom:6px;background:rgba(255,255,255,0.04)" />`;
      } else if (ptype === 'video' && (p.video?.link || p.video?.url)) {
        const u = safeImageUrl(p.video.link || p.video.url);
        if (u) tplHeader = html`<video controls playsinline preload="metadata"
          style="max-width:280px;border-radius:8px;display:block;margin-bottom:6px;background:#000">
          <source src=${u} /></video>`;
      } else if (ptype === 'document' && (p.document?.link || p.document?.url)) {
        const u  = safeImageUrl(p.document.link || p.document.url);
        const fn = p.document.filename || 'Document';
        if (u) tplHeader = html`<a href=${u} target="_blank" rel="noopener" download=${fn}
          style="display:inline-flex;align-items:center;gap:6px;text-decoration:none;color:inherit;background:rgba(255,255,255,0.05);padding:8px 12px;border-radius:8px;margin-bottom:6px;font-size:0.85rem">
          <span style="font-size:1rem">📄</span><span style="text-decoration:underline">${fn}</span></a>`;
      } else if (ptype === 'text' && p.text) {
        tplHeader = html`<div style="font-weight:600;font-size:0.95rem;margin-bottom:4px">${p.text}</div>`;
      }
    }

    // Body: if we have the rendered text (legacy), prefer it. Otherwise
    // surface the parameter values as a compact dot-separated list so the
    // agent at least sees the variables that were filled in.
    let tplBody = null;
    if (legacyTxt) {
      tplBody = html`<div style="font-size:0.88rem;line-height:1.45"><${FormattedText} text=${legacyTxt} /></div>`;
    } else if (bodyC?.parameters?.length) {
      const vals = bodyC.parameters.map(p => {
        const t = (p.type || '').toLowerCase();
        if (t === 'text')      return p.text || '';
        if (t === 'currency')  return `${p.currency?.code || ''} ${((p.currency?.amount_1000 || 0) / 1000)}`.trim();
        if (t === 'date_time') return p.date_time?.fallback_value || '';
        return '';
      }).filter(Boolean);
      if (vals.length) {
        tplBody = html`<div style="font-size:0.82rem;line-height:1.4;opacity:0.9">
          <span style="opacity:0.65">Variables:</span> ${vals.join(' · ')}
        </div>`;
      }
    }

    // Footer parameter — only present if the template has a dynamic footer.
    const footerTxt = footerC?.parameters?.[0]?.text || '';

    // Limited-time offer (Marketing Messages Lite). The component carries
    // expiration_time_ms — we surface "Limited offer · expires Jun 14 at
    // 14:30". The brand-amber accent matches WhatsApp's own LTO chip.
    let tplLto = null;
    if (ltoC) {
      const ltoP    = ltoC.parameters?.[0]?.limited_time_offer || {};
      const expMs   = Number(ltoP.expiration_time_ms || 0);
      let expLabel  = '';
      if (expMs > 0) {
        const d = new Date(expMs);
        if (!isNaN(d)) expLabel = d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
      }
      tplLto = html`
        <div style="display:inline-flex;align-items:center;gap:5px;background:rgba(245,158,11,0.15);border:1px solid rgba(245,158,11,0.35);color:#fbbf24;border-radius:6px;padding:4px 8px;font-size:0.74rem;font-weight:600;margin:6px 0">
          <span>⏳</span>
          <span>Limited offer${expLabel ? ` · expires ${expLabel}` : ''}</span>
        </div>
      `;
    }

    // Carousel (Marketing Messages Lite). Each card has its own header
    // (typically image) + body params + buttons. We render a horizontal
    // scrolling strip of small card thumbnails so the agent gets a sense
    // of what the customer sees, without going full-carousel widget.
    let tplCarousel = null;
    if (carouselC && Array.isArray(carouselC.cards) && carouselC.cards.length) {
      tplCarousel = html`
        <div style="display:flex;gap:8px;overflow-x:auto;padding:6px 2px;margin:4px -2px;scrollbar-width:none">
          ${carouselC.cards.map((card, ix) => {
            const cardComps   = Array.isArray(card.components) ? card.components : [];
            const cHeader     = cardComps.find(c => (c.type || '').toLowerCase() === 'header');
            const cBody       = cardComps.find(c => (c.type || '').toLowerCase() === 'body');
            const cButtons    = cardComps.filter(c => (c.type || '').toLowerCase() === 'button');
            const cHdrParam   = cHeader?.parameters?.[0] || {};
            const cHdrType    = (cHdrParam.type || '').toLowerCase();
            const cImageUrl   = cHdrType === 'image' ? safeImageUrl(cHdrParam.image?.link || cHdrParam.image?.url || '') : '';
            const cVideoUrl   = cHdrType === 'video' ? safeImageUrl(cHdrParam.video?.link || cHdrParam.video?.url || '') : '';
            const cBodyParams = (cBody?.parameters || []).map(p => p.text || p.currency?.code || p.date_time?.fallback_value || '').filter(Boolean);
            return html`
              <div key=${ix}
                style="flex:0 0 auto;width:160px;background:rgba(255,255,255,0.05);border:1px solid ${tplBdr};border-radius:8px;overflow:hidden;display:flex;flex-direction:column">
                ${cImageUrl
                  ? html`<img src=${cImageUrl} loading="lazy" alt="" style="width:100%;height:96px;object-fit:cover;display:block;background:rgba(255,255,255,0.04)" />`
                  : cVideoUrl
                    ? html`<video preload="metadata" muted playsinline style="width:100%;height:96px;object-fit:cover;display:block;background:#000"><source src=${cVideoUrl} /></video>`
                    : html`<div style="width:100%;height:60px;background:rgba(255,255,255,0.04);display:flex;align-items:center;justify-content:center;font-size:1.4rem;opacity:0.5">🖼️</div>`}
                <div style="padding:6px 8px;font-size:0.72rem;line-height:1.3">
                  <div style="opacity:0.55;font-size:0.62rem;text-transform:uppercase;letter-spacing:0.4px">Card ${ix + 1}</div>
                  ${cBodyParams.length > 0 && html`<div style="margin-top:2px;opacity:0.9;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden">${cBodyParams.join(' · ')}</div>`}
                  ${cButtons.length > 0 && html`<div style="margin-top:4px;font-size:0.66rem;opacity:0.7">${cButtons.length} action${cButtons.length === 1 ? '' : 's'}</div>`}
                </div>
              </div>
            `;
          })}
        </div>
      `;
    }

    // Buttons — we don't have the LABELS server-side (those live in the
    // template definition), so we surface sub_type as the identity hint.
    // URL buttons may carry the parameter as a path suffix appended to the
    // template's base URL; show it muted underneath as evidence.
    let tplButtons = null;
    if (buttonCs.length) {
      tplButtons = html`
        <div style="display:flex;flex-direction:column;gap:1px;border-top:1px solid ${tplBdr};margin-top:8px;margin-left:-10px;margin-right:-10px;margin-bottom:-6px">
          ${buttonCs.map(b => {
            const st = (b.sub_type || b.subtype || '').toLowerCase();
            const p  = b.parameters?.[0] || {};
            let label = 'Button';
            if (st === 'url')          label = '🔗 Open link';
            else if (st === 'quick_reply') label = '💬 Quick reply';
            else if (st === 'phone_number') label = '📞 Call';
            else if (st === 'copy_code') label = '⎘ Copy code';
            else if (st === 'flow')      label = '📋 Open form';
            else if (st === 'voice_call') label = '📞 Voice call';
            else if (st === 'otp')       label = '🔑 Auto-fill OTP';
            else if (st === 'catalog')   label = '🛒 View catalog';
            else if (st === 'mpm')       label = '🛒 View products';
            else if (st === 'order_details') label = '🧾 Order details';
            const hint = (st === 'url' && (p.text || p.payload))
              ? String(p.text || p.payload)
              : (st === 'copy_code' && (p.coupon_code || p.text)) ? String(p.coupon_code || p.text)
              : '';
            return html`
              <div style="padding:7px 10px;text-align:center;font-size:0.78rem;color:${tplFg};font-weight:600">
                ${label}${hint && html` <span style="opacity:0.6;font-weight:500;font-size:0.72rem">· ${hint.length > 32 ? hint.slice(0,32) + '…' : hint}</span>`}
              </div>
            `;
          })}
        </div>
      `;
    }

    if (!hasStruct && !legacyTxt) {
      bodyContent = html`<div style="font-size:0.85rem;opacity:0.85">
        📋 Template${humanName ? ` · ${humanName}` : ''}
      </div>`;
    } else {
      // Eyebrow chip — refined "TEMPLATE" badge + the humanized name in a
      // monospace tracking style. Brand-tinted on outbound (already inside
      // a brand-green bubble — readable white-on-green), accent-green on
      // inbound (against the dark bubble). Picks the right tint so the
      // chip never disappears against either background.
      const eyebrowFg  = isOut ? 'rgba(255,255,255,0.95)' : 'var(--v2-accent, #25D366)';
      const eyebrowBg  = isOut ? 'rgba(255,255,255,0.18)' : 'rgba(37,211,102,0.16)';
      const eyebrowBdr = isOut ? 'rgba(255,255,255,0.28)' : 'rgba(37,211,102,0.35)';
      bodyContent = html`
        ${humanName && html`
          <div class="inbox-tpl-eyebrow" style="display:inline-flex;align-items:center;gap:6px;font-size:0.61rem;font-weight:700;letter-spacing:0.13em;text-transform:uppercase;padding:3px 9px;border-radius:999px;background:${eyebrowBg};border:1px solid ${eyebrowBdr};color:${eyebrowFg};margin-bottom:7px">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="8" y1="13" x2="16" y2="13"/>
              <line x1="8" y1="17" x2="13" y2="17"/>
            </svg>
            <span>Template</span>
            <span style="opacity:0.65;font-weight:600">·</span>
            <span style="font-family:ui-monospace,SFMono-Regular,Menlo,monospace;letter-spacing:0.02em;text-transform:none">${humanName}</span>
            ${tplLang && html`<span style="opacity:0.55;font-weight:500;text-transform:uppercase">${tplLang}</span>`}
          </div>
        `}
        ${tplHeader}
        ${tplLto}
        ${tplBody}
        ${tplCarousel}
        ${footerTxt && html`<div style="font-size:0.7rem;opacity:0.7;margin-top:4px"><${FormattedText} text=${footerTxt} /></div>`}
        ${tplButtons}
      `;
    }
  } else if (type === 'interactive') {
    // #94 / #156: interactive messages flow in two directions with four
    // subtypes between them:
    //
    //   OUTBOUND (what we send via InteractivePicker):
    //     - 'button'                    → button template, N reply buttons
    //     - 'cta_url'                   → external link CTA
    //     - 'location_request_message'  → "send location" CTA
    //
    //   INBOUND (customer's tap/selection — they arrive as type='interactive'
    //            with interactive.type set to one of):
    //     - 'button_reply'              → they tapped one of our buttons
    //     - 'list_reply'                → they picked a row in our list message
    //     - 'nfm_reply'                 → they submitted a native flow form
    //
    // We RENDER the interactive object straight from the stored body. For
    // outbound, we mock the action area so the agent sees what the customer
    // will see. For inbound, we render the customer's selection as the
    // primary content so the agent can see which option was chosen.
    const it = msg.interactive || {};
    const header = it.header?.type === 'text' ? it.header.text : null;
    const body   = it.body?.text || '';
    const footer = it.footer?.text || '';
    const itype  = it.type || '';

    // Color tokens — #156: the old hardcoded #60a5fa / #25D366 were
    // invisible on the brand-green outbound bubble. Swap to white-ish on
    // outbound, keep the blue/green accent on inbound (dark bubble).
    const actionFg   = isOut ? 'rgba(255,255,255,0.95)'  : '#60a5fa';
    const actionFgGr = isOut ? 'rgba(255,255,255,0.95)'  : '#25D366';
    const actionBdr  = isOut ? 'rgba(255,255,255,0.28)'  : 'rgba(255,255,255,0.18)';

    let actionArea = null;
    let replyBlock = null;

    if (itype === 'button' && it.action?.buttons?.length) {
      actionArea = html`
        <div style="display:flex;flex-direction:column;gap:1px;border-top:1px solid ${actionBdr};margin-top:8px;margin-left:-10px;margin-right:-10px;margin-bottom:-6px">
          ${it.action.buttons.map(b => html`
            <div style="padding:7px 10px;text-align:center;font-size:0.78rem;color:${actionFg};font-weight:600">${b.reply?.title || ''}</div>
          `)}
        </div>
      `;
    } else if (itype === 'list') {
      // S22 — WhatsApp list message (outbound). Action carries a button
      // label that opens a sheet on the customer's phone; sections hold
      // the actual selectable rows. Agent preview = the button label +
      // a compact row count so they can verify the list is right.
      const btnLabel = it.action?.button || 'View options';
      const sections = Array.isArray(it.action?.sections) ? it.action.sections : [];
      const rowCount = sections.reduce((n, s) => n + (s.rows?.length || 0), 0);
      actionArea = html`
        <div style="border-top:1px solid ${actionBdr};margin-top:8px;margin-left:-10px;margin-right:-10px;margin-bottom:-6px;padding:7px 10px;text-align:center;font-size:0.78rem;color:${actionFg};font-weight:600;display:flex;align-items:center;justify-content:center;gap:5px">
          <span>⋮</span>
          <span>${btnLabel}</span>
          ${rowCount > 0 && html`<span style="opacity:0.6;font-weight:500">· ${rowCount} option${rowCount === 1 ? '' : 's'}</span>`}
        </div>
      `;
    } else if (itype === 'cta_url') {
      const labelTxt = it.action?.parameters?.display_text || 'Open link';
      actionArea = html`
        <div style="border-top:1px solid ${actionBdr};margin-top:8px;margin-left:-10px;margin-right:-10px;margin-bottom:-6px;padding:7px 10px;text-align:center;font-size:0.78rem;color:${actionFg};font-weight:600">
          🔗 ${labelTxt}
        </div>
      `;
    } else if (itype === 'flow') {
      // S22 — Native Flow launcher. Meta exposes the CTA label under
      // action.parameters.flow_cta (authoritative) or older payloads
      // use action.button — we accept either so historical messages
      // don't render blank.
      const p = it.action?.parameters || {};
      const flowCta = p.flow_cta || it.action?.button || 'Open form';
      actionArea = html`
        <div style="border-top:1px solid ${actionBdr};margin-top:8px;margin-left:-10px;margin-right:-10px;margin-bottom:-6px;padding:7px 10px;text-align:center;font-size:0.78rem;color:${actionFg};font-weight:600;display:flex;align-items:center;justify-content:center;gap:5px">
          📋 ${flowCta}
        </div>
      `;
    } else if (itype === 'location_request_message') {
      actionArea = html`
        <div style="border-top:1px solid ${actionBdr};margin-top:8px;margin-left:-10px;margin-right:-10px;margin-bottom:-6px;padding:7px 10px;text-align:center;font-size:0.78rem;color:${actionFgGr};font-weight:600;display:flex;align-items:center;justify-content:center;gap:5px">
          📍 Send location
        </div>
      `;
    } else if (itype === 'address_message') {
      // S22 — India-only address collection CTA. Customer taps → sheet
      // opens with saved addresses or a blank form. Agent preview = a
      // single row with the house glyph.
      actionArea = html`
        <div style="border-top:1px solid ${actionBdr};margin-top:8px;margin-left:-10px;margin-right:-10px;margin-bottom:-6px;padding:7px 10px;text-align:center;font-size:0.78rem;color:${actionFgGr};font-weight:600;display:flex;align-items:center;justify-content:center;gap:5px">
          🏠 Provide address
        </div>
      `;
    } else if (itype === 'product' || itype === 'product_list') {
      // S22 — Meta Commerce catalog. 'product' = single item card,
      // 'product_list' = multi-section catalog. We can't render the
      // actual imagery without a catalog lookup (Commerce Graph API
      // call + caching), so we surface a recognizable stub carrying
      // either the retailer SKU (single) or section count (list).
      const singleRetailerId = itype === 'product' ? (it.action?.product_retailer_id || '') : '';
      const sectionCount = itype === 'product_list'
        ? (Array.isArray(it.action?.sections) ? it.action.sections.length : 0)
        : 0;
      const subLabel = itype === 'product'
        ? (singleRetailerId ? `SKU ${singleRetailerId}` : 'Single product')
        : `${sectionCount} section${sectionCount === 1 ? '' : 's'}`;
      actionArea = html`
        <div style="border-top:1px solid ${actionBdr};margin-top:8px;margin-left:-10px;margin-right:-10px;margin-bottom:-6px;padding:7px 10px;text-align:center;font-size:0.78rem;color:${actionFg};font-weight:600;display:flex;align-items:center;justify-content:center;gap:5px;flex-wrap:wrap">
          <span>🛒</span>
          <span>${itype === 'product' ? 'Product' : 'Catalog'}</span>
          ${subLabel && html`<span style="opacity:0.6;font-weight:500">· ${subLabel}</span>`}
        </div>
      `;
    } else if (itype === 'button_reply') {
      // Customer tapped one of our buttons — render the tapped title with
      // the WhatsApp "quick reply" icon (corner-down-left curved arrow,
      // same glyph WhatsApp uses in its own thread view next to button
      // replies). NOT a check mark — that's for status ticks.
      const title = it.button_reply?.title || '';
      const quickReplyIcon = html`
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
             stroke=${isOut ? '#fff' : '#25D366'}
             stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"
             style="flex-shrink:0">
          <polyline points="9 10 4 15 9 20"/>
          <path d="M20 4v7a4 4 0 0 1-4 4H4"/>
        </svg>
      `;
      replyBlock = html`
        <div style="display:inline-flex;align-items:center;gap:7px;font-size:0.88rem;line-height:1.4">
          ${quickReplyIcon}
          <span style="font-weight:600">${title || '[button]'}</span>
        </div>
      `;
    } else if (itype === 'list_reply') {
      // Customer picked a row in our list message — title is the row label,
      // description (optional) is the secondary text. Same quick-reply
      // curved-arrow glyph as button_reply above.
      const title = it.list_reply?.title || '';
      const desc  = it.list_reply?.description || '';
      const quickReplyIcon = html`
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
             stroke=${isOut ? '#fff' : '#25D366'}
             stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"
             style="flex-shrink:0">
          <polyline points="9 10 4 15 9 20"/>
          <path d="M20 4v7a4 4 0 0 1-4 4H4"/>
        </svg>
      `;
      replyBlock = html`
        <div style="font-size:0.88rem;line-height:1.4">
          <div style="display:flex;align-items:center;gap:7px">
            ${quickReplyIcon}
            <span style="font-weight:600">${title || '[list selection]'}</span>
          </div>
          ${desc && html`<div style="font-size:0.75rem;opacity:0.75;margin-top:3px;margin-left:23px">${desc}</div>`}
        </div>
      `;
    } else if (itype === 'nfm_reply') {
      // Customer submitted a native flow form. response_json holds the
      // field answers as stringified JSON. S22 — parse it defensively
      // and render each non-empty field as "label: value" so the agent
      // can read the actual submission without opening a debug panel.
      // Falls back to the old "Submitted {name}" stub if JSON parse
      // fails or the response is empty (defensive — older submissions
      // may have shapes we don't fully understand).
      const name = it.nfm_reply?.name || 'form';
      let fields = null;
      try {
        const raw = it.nfm_reply?.response_json;
        if (raw && typeof raw === 'string') {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') {
            // Skip noise keys that Meta injects (flow_token, version, etc).
            const SKIP = new Set(['flow_token', 'version']);
            fields = Object.entries(parsed)
              .filter(([k, v]) => !SKIP.has(k) && v != null && v !== '')
              .map(([k, v]) => ({
                // Humanize Meta's screen_N_Input_M keys into "Input M".
                label: String(k).replace(/^screen_\d+_/, '').replace(/_/g, ' '),
                value: typeof v === 'object' ? JSON.stringify(v) : String(v),
              }));
          }
        }
      } catch { /* fall through to stub */ }

      replyBlock = html`
        <div style="font-size:0.88rem;line-height:1.4">
          <div style="display:inline-flex;align-items:center;gap:6px;margin-bottom:${fields?.length ? '6px' : '0'}">
            <span style="font-size:1rem">📋</span>
            <span style="font-weight:600">Submitted ${name}</span>
          </div>
          ${fields?.length > 0 && html`
            <div style="display:flex;flex-direction:column;gap:4px;padding-left:22px">
              ${fields.map(f => html`
                <div style="font-size:0.78rem;line-height:1.3">
                  <span style="opacity:0.65;text-transform:capitalize">${f.label}:</span>
                  <span style="font-weight:500;margin-left:4px;word-break:break-word">${f.value.length > 80 ? f.value.slice(0, 80) + '…' : f.value}</span>
                </div>
              `)}
            </div>
          `}
        </div>
      `;
    } else if (itype) {
      // Unknown subtype — render a visible debug hint so we notice new
      // WhatsApp interactive types instead of silently rendering nothing.
      replyBlock = html`<div style="font-size:0.85rem;opacity:0.8">[interactive · ${itype}]</div>`;
    }

    bodyContent = html`
      ${header && html`<div style="font-weight:600;font-size:0.92rem;margin-bottom:4px"><${FormattedText} text=${header} /></div>`}
      ${body && html`<div style="font-size:0.85rem;line-height:1.45"><${FormattedText} text=${body} /></div>`}
      ${footer && html`<div style="font-size:0.7rem;opacity:0.7;margin-top:4px"><${FormattedText} text=${footer} /></div>`}
      ${replyBlock}
      ${actionArea}
    `;
  } else {
    bodyContent = html`<div style="font-size:0.85rem;opacity:0.7">[${type}]</div>`;
  }

  // Status tick (outbound only)
  const tickChar = TICKS[msg.status] || '';
  const tickClass = msg.status === MESSAGE_STATUS.FAILED    ? 'inbox-tick-failed'
                  : msg.status === MESSAGE_STATUS.READ      ? 'inbox-tick-read'
                  : msg.status === MESSAGE_STATUS.DELIVERED ? 'inbox-tick-delivered'
                  : 'inbox-tick-sent';

  // Reply-quote styling. Color-codes by who sent the ORIGINAL message:
  //   - fromMe (you replied to your own message OR customer replied to your
  //     message)  → green accent ("You")
  //   - !fromMe (you replied to a customer message OR customer replied to
  //     their own message) → muted accent ("Customer")
  // The text is precomputed in Thread.js using the wamid index, so we just
  // render it. Falls back to "Original message" when target wasn't in the
  // loaded window.
  // Reply quote accent color — drives the left border + label text.
  // Phase D: when the parent bubble is OUTBOUND (bright brand green) the
  // brand-green accent collides with the bubble background and disappears.
  // For outbound parents, swap the accent to a high-contrast off-white so
  // the label + border read cleanly. The CSS in inbox-v2.css handles the
  // quote background + body text contrast separately via the
  // .inbox-bubble-out .inbox-bubble-quote selector chain.
  const isOutboundBubble = isOut;
  const quoteAccent = isOutboundBubble
    ? 'rgba(255, 255, 255, 0.92)'
    : (replyToPreview?.fromMe ? '#25D366' : '#a4adc1');
  const quoteLabel  = replyToPreview?.fromMe ? 'You'     : 'Customer';

  return html`
    <div
      class="inbox-bubble-wrap ${wrapClass}"
      data-wamid=${msg.wamid || msg.id}
      data-meta-wamid=${msg.metaWamid || ''}
    >
      <div
        class="inbox-bubble ${bubbleClass}"
        onContextMenu=${handleContextMenu}
        onTouchStart=${handleTouchStart}
        onTouchMove=${handleTouchMove}
        onTouchEnd=${handleTouchEnd}
        onTouchCancel=${handleTouchEnd}
        onClickCapture=${handleClickCapture}
        style="-webkit-user-select:none;user-select:none;-webkit-touch-callout:none;-webkit-tap-highlight-color:transparent"
      >
        ${msg.replyTo && replyToPreview && html`
          <div
            class="inbox-bubble-quote ${replyToPreview.found ? 'inbox-bubble-quote-jumpable' : ''}"
            style="border-left-color:${quoteAccent};${replyToPreview.found ? 'cursor:pointer' : ''}"
            onClick=${(e) => {
              // #161 — tap the reply-quote to jump-scroll to the
              // original message. Only fires when the target is
              // actually loaded in the current window (replyToPreview
              // .found=true). If the target isn't loaded we don't
              // pretend to be interactive — no cursor, no handler.
              if (!replyToPreview.found || !onJumpToReply) return;
              // Stop propagation so the click doesn't also trigger
              // the parent bubble's onClickCapture reaction-shelf path.
              e.stopPropagation();
              onJumpToReply(msg.replyTo);
            }}
            title=${replyToPreview.found ? 'Jump to original message' : ''}
          >
            <span class="inbox-bubble-quote-label" style="color:${quoteAccent}">
              ${quoteLabel}
            </span>
            <span class="inbox-bubble-quote-text">
              ${replyToPreview.text || '[Message]'}
            </span>
          </div>
        `}
        ${bodyContent}
      </div>
      ${reactionList.length > 0 && html`
        <div class="inbox-bubble-reactions" style="display:flex;gap:4px;flex-wrap:wrap;margin-top:-10px;margin-bottom:6px;position:relative;z-index:3;pointer-events:auto;${isOut ? 'justify-content:flex-end;padding-right:10px' : 'padding-left:10px'}">
          ${reactionList.map(r => html`
            <span
              key=${r.emoji}
              class="inbox-reaction-chip ${r.mine ? 'mine' : ''}"
              style="background:${r.mine ? '#1f5a3a' : '#2b3340'};border:1.5px solid var(--inbox-bg, #0a0c12);border-radius:14px;padding:2px 8px;font-size:0.82rem;line-height:1.2;display:inline-flex;align-items:center;gap:3px;box-shadow:0 1px 4px rgba(0,0,0,0.4);color:#fff"
              title=${r.mine && !interactionsLocked ? 'Tap to remove' : ''}
              onClick=${r.mine && onReact && !interactionsLocked ? () => onReact('') : undefined}
            >
              ${r.emoji}${r.count > 1 ? html`<span style="font-size:0.7rem;opacity:0.9">${r.count}</span>` : ''}
            </span>
          `)}
        </div>
      `}
      ${!groupedBottom && html`
        <div class="inbox-bubble-meta">
          ${formatMsgTime(msg.timestamp)}
          ${isOut && tickChar && html` <span class="inbox-tick ${tickClass}">${tickChar}</span>`}
          ${msg.status === MESSAGE_STATUS.FAILED && html` <span style="color:#ef4444;font-size:0.7rem">Failed</span>`}
        </div>
      `}

      ${reactionShelfOpen && html`
        <div style="position:fixed;inset:0;z-index:200" onClick=${() => setReactionShelfOpen(false)}></div>
        <div class="inbox-reaction-shelf-popup" style="display:flex;position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:201">
          ${REACTIONS.map(e => html`
            <button key=${e} class="inbox-react-btn" onClick=${() => handleReact(e)}>${e}</button>
          `)}
          <div class="inbox-reaction-shelf-sep"></div>
          <button class="inbox-react-btn inbox-react-reply" onClick=${handleReply} title="Reply">↩</button>
          ${_copyableText() && html`
            <button
              class="inbox-react-btn inbox-react-copy"
              onClick=${handleCopy}
              title="Copy"
              aria-label="Copy message text"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </button>
          `}
        </div>
      `}

      ${lightbox && html`
        <${MediaLightbox}
          url=${lightbox.url}
          mimeType=${lightbox.mimeType}
          filename=${lightbox.filename}
          onClose=${() => setLightbox(null)}
        />
      `}
    </div>
  `;
}
